import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Common/button';

import * as yaml from 'js-yaml';

interface Theme {
  id: string;
  code: string;
  label: string;
  description: string;
  icon: string;
}

interface ThemesFormProps {
  initialContent?: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

export function ThemesForm({ initialContent, onSave, onCancel }: ThemesFormProps) {
  const { t } = useTranslation();
  const [themes, setThemes] = useState<Theme[]>([]);
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = yaml.load(initialContent) as { valid_themes: Omit<Theme, 'id'>[] };
        if (parsed?.valid_themes && Array.isArray(parsed.valid_themes)) {
          const themesWithIds = parsed.valid_themes.map((theme, index) => ({
            ...theme,
            id: `theme-${Date.now()}-${index}`,
          }));
          setThemes(themesWithIds);
        }
      } catch (error) {
        console.error('Error parsing themes YAML:', error);
      }
    }
  }, [initialContent]);

  const handleAddTheme = () => {
    const newTheme: Theme = {
      id: `theme-${Date.now()}`,
      code: '',
      label: '',
      description: '',
      icon: 'FileText',
    };
    setThemes([newTheme, ...themes]);
    setExpandedThemes(new Set([...expandedThemes, newTheme.id]));
  };

  const handleUpdateTheme = (id: string, field: keyof Omit<Theme, 'id'>, value: string) => {
    setThemes(themes.map(theme =>
      theme.id === id ? { ...theme, [field]: value } : theme
    ));
  };

  const handleDeleteTheme = (id: string) => {
    setThemes(themes.filter(theme => theme.id !== id));
    const newExpanded = new Set(expandedThemes);
    newExpanded.delete(id);
    setExpandedThemes(newExpanded);
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedThemes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedThemes(newExpanded);
  };

  const handleSave = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const themesData = themes.map(({ id: _id, ...theme }) => theme);

    const yamlContent = yaml.dump(
      { valid_themes: themesData },
      {
        indent: 2,
        lineWidth: -1,
        noRefs: true,
        sortKeys: false,
      }
    );

    onSave(yamlContent);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <p className="text-sm text-muted-foreground">
            {t('admin.configForms.themes.intro')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('admin.configForms.themes.hint')}
          </p>
        </div>
        <Button
          onClick={handleAddTheme}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('admin.configForms.themes.addTheme')}
        </Button>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {themes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('admin.configForms.themes.empty')}
          </div>
        ) : (
          themes.map((theme) => (
            <div
              key={theme.id}
              className="border border-border rounded-lg overflow-hidden bg-card"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                onClick={() => toggleExpanded(theme.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {theme.code || 'new_theme'}
                    </code>
                    <span className="font-medium truncate">
                      {theme.label || t('admin.configForms.shared.untitled')}
                    </span>
                    {theme.icon && (
                      <span className="text-xs text-muted-foreground">
                        {theme.icon}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTheme(theme.id);
                    }}
                    className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {expandedThemes.has(theme.id) ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
              </div>

              {expandedThemes.has(theme.id) && (
                <div className="p-4 pt-0 space-y-4 border-t border-border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.shared.code')}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={theme.code}
                        onChange={(e) => handleUpdateTheme(theme.id, 'code', e.target.value)}
                        placeholder={t('admin.configForms.themes.codePlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('admin.configForms.shared.snakeCaseHint')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t('admin.configForms.shared.icon')}
                      </label>
                      <input
                        type="text"
                        value={theme.icon}
                        onChange={(e) => handleUpdateTheme(theme.id, 'icon', e.target.value)}
                        placeholder={t('admin.configForms.themes.iconPlaceholder')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('admin.configForms.themes.iconHint')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('admin.configForms.shared.label')}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={theme.label}
                      onChange={(e) => handleUpdateTheme(theme.id, 'label', e.target.value)}
                      placeholder={t('admin.configForms.themes.labelPlaceholder')}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('admin.configForms.themes.labelHint')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {t('global.description')}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={theme.description}
                      onChange={(e) => handleUpdateTheme(theme.id, 'description', e.target.value)}
                      placeholder={t('admin.configForms.themes.descriptionPlaceholder')}
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm resize-none"
                    />
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex gap-2 justify-end pt-4 border-t border-border">
        <Button onClick={onCancel} variant="outline">
          {t('actions.cancel')}
        </Button>
        <Button onClick={handleSave}>
          {t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
